export function normalizeMedicalInstitutionSpecialtyText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

export function normalizeMedicalInstitutionCustomSpecialtyLabel(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function normalizeMedicalInstitutionSpecialties(specialties = []) {
  const seen = new Set();
  return (Array.isArray(specialties) ? specialties : []).reduce((catalogue, specialty) => {
    const id = String(specialty?.id || "").trim();
    const name = String(specialty?.name || "").trim();
    if (!id || !name || seen.has(id)) return catalogue;
    seen.add(id);
    catalogue.push({
      id,
      name,
      code: String(specialty?.code || "").trim(),
      aliases: Array.isArray(specialty?.aliases)
        ? specialty.aliases.map((alias) => String(alias || "").trim()).filter(Boolean)
        : [],
      source: String(specialty?.source || "canonical"),
      reviewStatus: String(specialty?.reviewStatus || specialty?.review_status || "approved"),
    });
    return catalogue;
  }, []);
}

export function normalizeMedicalInstitutionSpecialtyIds(selectedIds = [], specialties = []) {
  const validIds = new Set(normalizeMedicalInstitutionSpecialties(specialties).map((specialty) => specialty.id));
  const seen = new Set();
  return (Array.isArray(selectedIds) ? selectedIds : []).reduce((ids, rawId) => {
    const id = String(rawId || "").trim();
    if (!id || !validIds.has(id) || seen.has(id)) return ids;
    seen.add(id);
    ids.push(id);
    return ids;
  }, []);
}

export function normalizeMedicalInstitutionCustomSpecialties(customSpecialties = [], canonicalSpecialties = []) {
  const canonicalNames = new Set(normalizeMedicalInstitutionSpecialties(canonicalSpecialties).map((item) => normalizeMedicalInstitutionSpecialtyText(item.name)));
  const seen = new Set();
  return (Array.isArray(customSpecialties) ? customSpecialties : []).reduce((items, rawItem) => {
    const name = normalizeMedicalInstitutionCustomSpecialtyLabel(rawItem?.name || rawItem);
    const normalizedName = normalizeMedicalInstitutionSpecialtyText(name);
    if (!name || normalizedName.length < 2 || canonicalNames.has(normalizedName) || seen.has(normalizedName)) return items;
    seen.add(normalizedName);
    items.push({
      id: String(rawItem?.id || `custom:${normalizedName}`),
      name,
      normalizedName,
      source: "user_added",
      reviewStatus: "pending_review",
    });
    return items;
  }, []);
}

export function getMedicalInstitutionSpecialtyState({ specialties = [], selectedIds = [], customSpecialties = [], query = "" } = {}) {
  const catalogue = normalizeMedicalInstitutionSpecialties(specialties);
  const normalizedSelectedIds = normalizeMedicalInstitutionSpecialtyIds(selectedIds, catalogue);
  const selectedIdSet = new Set(normalizedSelectedIds);
  const normalizedCustom = normalizeMedicalInstitutionCustomSpecialties(customSpecialties, catalogue);
  const normalizedQuery = normalizeMedicalInstitutionSpecialtyText(query);
  const selectedCanonical = catalogue.filter((specialty) => selectedIdSet.has(specialty.id));
  const selected = [...selectedCanonical, ...normalizedCustom];
  const available = catalogue.filter((specialty) => !selectedIdSet.has(specialty.id));
  const results = available.filter((specialty) => {
    // This picker is deliberately query-gated. A full catalogue is not a
    // useful or accessible default in the compact Institution flow.
    if (!normalizedQuery) return false;
    return [specialty.name, specialty.code, ...specialty.aliases]
      .map(normalizeMedicalInstitutionSpecialtyText)
      .some((value) => value.includes(normalizedQuery));
  });
  const exactCanonicalOrCustom = [...catalogue, ...normalizedCustom].some((specialty) => normalizeMedicalInstitutionSpecialtyText(specialty.name) === normalizedQuery);
  return {
    catalogue,
    selected,
    selectedIds: normalizedSelectedIds,
    customSpecialties: normalizedCustom,
    available,
    results,
    allAvailableSelected: catalogue.length > 0 && available.length === 0,
    canAddCustom: Boolean(normalizedQuery) && !exactCanonicalOrCustom,
    customLabel: normalizeMedicalInstitutionCustomSpecialtyLabel(query),
  };
}

export function addMedicalInstitutionSpecialtyId(selectedIds = [], specialtyId, specialties = []) {
  const current = normalizeMedicalInstitutionSpecialtyIds(selectedIds, specialties);
  const id = String(specialtyId || "").trim();
  const validIds = new Set(normalizeMedicalInstitutionSpecialties(specialties).map((specialty) => specialty.id));
  if (!id || !validIds.has(id) || current.includes(id)) return current;
  return [...current, id];
}

export function removeMedicalInstitutionSpecialtyId(selectedIds = [], specialtyId, specialties = []) {
  const id = String(specialtyId || "").trim();
  return normalizeMedicalInstitutionSpecialtyIds(selectedIds, specialties).filter((current) => current !== id);
}

export function addMedicalInstitutionCustomSpecialty(customSpecialties = [], label, canonicalSpecialties = []) {
  return normalizeMedicalInstitutionCustomSpecialties([...customSpecialties, { name: label }], canonicalSpecialties);
}

export function removeMedicalInstitutionCustomSpecialty(customSpecialties = [], specialtyId, canonicalSpecialties = []) {
  const id = String(specialtyId || "").trim();
  return normalizeMedicalInstitutionCustomSpecialties(customSpecialties, canonicalSpecialties).filter((item) => item.id !== id);
}
